const { applySections, config, emit } = window.nt;

/**
 * <cart-items> — quantity changes and removals of cart lines, in the cart drawer and on the cart page.
 *
 * The lines are inside a plain form, so everything still works without JavaScript. With it, a change is sent to
 * the Cart API together with the ids of the sections to refresh (data-sections), and the fresh markup is swapped
 * in from the same response: one request updates the lines, the totals and the header count.
 */
class CartItems extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', this.onChange);
    this.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
    this.removeEventListener('click', this.onClick);
  }

  onChange = (event) => {
    const input = event.target.closest('[data-keys]');
    if (!input) return;
    this.update(input.dataset.keys.split(','), Number(input.value), input);
  };

  onClick = (event) => {
    const remove = event.target.closest('[data-remove]');
    if (!remove) return;
    event.preventDefault();
    this.update(remove.dataset.keys.split(','), 0);
  };

  /**
   * Sets the total quantity of one product row. A row may be made of several cart lines (a discount can split
   * one line into portions), so the first line gets the whole quantity and the others are emptied, all in a
   * single request: the discount is then worked out again on the new total.
   */
  async update(keys, quantity, input) {
    if (this.busy) return;
    this.busy = true;
    this.setBusy(true);
    this.showError('');

    try {
      const updates = Object.fromEntries(keys.map((key, index) => [key, index === 0 ? quantity : 0]));
      const response = await fetch(config.routes.cartUpdate, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates, sections: this.dataset.sections }),
      });
      const cart = await response.json();
      if (!response.ok || cart.status) throw new Error(cart.description || cart.message || 'error');

      // The requested quantity may have been reduced to the available stock: the fresh markup shows the truth.
      applySections(cart.sections);
      emit('cart:updated', { source: 'cart-items', cart, sections: cart.sections });
    } catch (error) {
      if (input) input.value = input.defaultValue;
      this.showError(error.message);
    } finally {
      this.busy = false;
      this.setBusy(false);
    }
  }

  setBusy(busy) {
    this.toggleAttribute('aria-busy', busy);
  }

  showError(message) {
    const target = this.querySelector('[data-cart-error]');
    if (!target) return;
    target.textContent = message;
    target.hidden = !message;
  }
}

if (!customElements.get('cart-items')) customElements.define('cart-items', CartItems);
